import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCirclePlus, 
    faPenToSquare, 
    faTrashCan, 
    faRoute,
    faMapMarkedAlt,
    faPlus,
    faChevronDown,
    faChevronUp 
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import api from "../../api/api";

export function TourManager() {
    const { t } = useTranslation();
    const [tours, setTours] = useState([]);
    const [medals, setMedals] = useState([]);
    const [airports, setAirports] = useState([]);
    const [error, setError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showLegsForm, setShowLegsForm] = useState(false);
    const [selectedTour, setSelectedTour] = useState(null);
    const [tourLegs, setTourLegs] = useState([]);
    const [expandedTour, setExpandedTour] = useState(null);
    
    const [formData, setFormData] = useState({
        medalId: '',
        img: '',
        name: '',
        description: '',
        shortDescription: '',
        open_day: '',
        close_day: '',
        recommendedAircrafts: '',
        bannerImage: '',
        isActive: true
    });
    
    const [legFormData, setLegFormData] = useState({
        departureIcao: '',
        arrivalIcao: '',
        estimatedTime: ''
    });
    
    const [editingTour, setEditingTour] = useState(null);

    useEffect(() => {
        fetchTours();
        fetchMedals();
        fetchAirports();
    }, []);

    const fetchTours = async () => {
        try {
            const response = await api.get('/tours');
            setTours(response.data);
            setError(null);
        } catch (error) {
            console.error('Failed to fetch tours:', error);
            setError('Failed to fetch tours');
        }
    };

    const fetchMedals = async () => {
        try {
            const response = await api.get('/medals');
            setMedals(response.data);
        } catch (error) {
            console.error('Failed to fetch medals:', error);
        }
    };

    const fetchAirports = async () => {
        try {
            const response = await api.get('/airports');
            // The endpoint returns { data: airports[] }
            const airportsList = response.data.data || response.data;
            // Only get first 100 for performance
            setAirports(Array.isArray(airportsList) ? airportsList.slice(0, 100) : []);
        } catch (error) {
            console.error('Failed to fetch airports:', error);
        }
    };

    const fetchTourLegs = async (tourId) => {
        try {
            const response = await api.get(`/legs/tour/${tourId}`);
            setTourLegs(response.data);
        } catch (error) {
            console.error('Failed to fetch tour legs:', error);
        }
    };

    const toggleCreate = () => {
        setShowCreateForm(!showCreateForm);
        setFormData({
            medalId: '',
            img: '',
            name: '',
            description: '',
            shortDescription: '',
            open_day: '',
            close_day: '',
            recommendedAircrafts: '',
            bannerImage: '',
            isActive: true
        });
        setEditingTour(null);
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleLegInputChange = (e) => {
        const { name, value } = e.target;
        setLegFormData(prev => ({
            ...prev,
            [name]: value.toUpperCase()
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (new Date(formData.close_day) <= new Date(formData.open_day)) {
            setError('Close date must be after open date');
            return;
        }

        try {
            const payload = {
                ...formData,
                medalId: formData.medalId ? parseInt(formData.medalId) : null,
                open_day: new Date(formData.open_day).toISOString(),
                close_day: new Date(formData.close_day).toISOString(),
                recommendedAircrafts: formData.recommendedAircrafts ? 
                    JSON.stringify(formData.recommendedAircrafts.split(',').map(a => a.trim())) : 
                    null
            };

            if (editingTour) {
                const response = await api.patch(`/tours/${editingTour.id}`, payload);
                setTours(tours.map(tour => tour.id === editingTour.id ? response.data.tour : tour));
                alert('Tour updated successfully');
            } else {
                const response = await api.post('/tours', payload);
                setTours([...tours, response.data.tour]);
                alert('Tour created successfully');
            }
            toggleCreate();
        } catch (error) {
            console.error('Failed to save tour:', error);
            setError(error.response?.data?.error || 'Failed to save tour');
        }
    };

    const handleAddLeg = async (e) => {
        e.preventDefault();
        
        if (!selectedTour) return;

        try {
            const nextOrder = tourLegs.length + 1;
            const payload = {
                tourId: selectedTour.id,
                order: nextOrder,
                airportDepartureIcao: legFormData.departureIcao,
                airportArrivalIcao: legFormData.arrivalIcao,
                estimatedTime: legFormData.estimatedTime ? parseInt(legFormData.estimatedTime) : null,
                description: legFormData.description
            };

            const response = await api.post('/legs', payload);
            setTourLegs([...tourLegs, response.data.leg]);
            
            // Clear form
            setLegFormData({
                departureIcao: '',
                arrivalIcao: '',
                estimatedTime: '',
                description: ''
            });
            
            alert('Leg added successfully');
        } catch (error) {
            console.error('Failed to add leg:', error);
            alert(error.response?.data?.error || 'Failed to add leg');
        }
    };

    const handleDeleteLeg = async (legId) => {
        if (window.confirm('Are you sure you want to delete this leg?')) {
            try {
                await api.delete(`/legs/${legId}`);
                setTourLegs(tourLegs.filter(leg => leg.id !== legId));
                alert('Leg deleted successfully');
            } catch (error) {
                console.error('Failed to delete leg:', error);
                alert('Failed to delete leg');
            }
        }
    };

    const startEditing = (tour) => {
        setEditingTour(tour);
        
        let recommendedAircrafts = '';
        try {
            if (tour.recommendedAircrafts) {
                const aircrafts = JSON.parse(tour.recommendedAircrafts);
                recommendedAircrafts = Array.isArray(aircrafts) ? aircrafts.join(', ') : '';
            }
        } catch (e) {
            recommendedAircrafts = tour.recommendedAircrafts || '';
        }

        setFormData({
            medalId: tour.medalId || '',
            img: tour.img || '',
            name: tour.name || '',
            description: tour.description || '',
            shortDescription: tour.shortDescription || '',
            open_day: tour.open_day ? new Date(tour.open_day).toISOString().slice(0, 16) : '',
            close_day: tour.close_day ? new Date(tour.close_day).toISOString().slice(0, 16) : '',
            recommendedAircrafts: recommendedAircrafts,
            bannerImage: tour.bannerImage || '',
            isActive: tour.isActive !== undefined ? tour.isActive : true
        });
        setShowCreateForm(true);
        setError(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this tour? This will also delete all its legs.')) {
            try {
                await api.delete(`/tours/${id}`);
                setTours(tours.filter(tour => tour.id !== id));
                alert('Tour deleted successfully');
            } catch (error) {
                console.error('Failed to delete tour:', error);
                alert('Failed to delete tour');
            }
        }
    };

    const toggleExpandTour = async (tour) => {
        if (expandedTour === tour.id) {
            setExpandedTour(null);
            setTourLegs([]);
        } else {
            setExpandedTour(tour.id);
            await fetchTourLegs(tour.id);
        }
    };

    const openLegsManager = async (tour) => {
        setSelectedTour(tour);
        setShowLegsForm(true);
        await fetchTourLegs(tour.id);
    };

    return (
        <div className="aircraft-manager">
            <div className="manager-header">
                <h1><FontAwesomeIcon icon={faRoute} /> {t('tour-manager')}</h1>
                <button className="btn btn-primary" onClick={toggleCreate}>
                    <FontAwesomeIcon icon={faCirclePlus} /> {editingTour ? t('edit-tour') : t('create-tour')}
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {showCreateForm ? (
                <form onSubmit={handleSubmit} className="create-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('name')} *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                maxLength={100}
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('medal')} ({t('optional')})</label>
                            <select
                                name="medalId"
                                value={formData.medalId}
                                onChange={handleInputChange}
                            >
                                <option value="">{t('no-medal')}</option>
                                {medals.map(medal => (
                                    <option key={medal.id} value={medal.id}>
                                        {medal.text}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('short-description')} *</label>
                            <input
                                type="text"
                                name="shortDescription"
                                value={formData.shortDescription}
                                onChange={handleInputChange}
                                required
                                maxLength={255}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group full-width">
                            <label>{t('full-description')} *</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                rows={4}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('image-url')} *</label>
                            <input
                                type="text"
                                name="img"
                                value={formData.img}
                                onChange={handleInputChange}
                                required
                                maxLength={500}
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('banner-image-url')} ({t('optional')})</label>
                            <input
                                type="text"
                                name="bannerImage"
                                value={formData.bannerImage}
                                onChange={handleInputChange}
                                maxLength={500}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('open-date')} *</label>
                            <input
                                type="datetime-local"
                                name="open_day"
                                value={formData.open_day}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('close-date')} *</label>
                            <input
                                type="datetime-local"
                                name="close_day"
                                value={formData.close_day}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('recommended-aircraft-comma-separated')}</label>
                            <input
                                type="text"
                                name="recommendedAircrafts"
                                value={formData.recommendedAircrafts}
                                onChange={handleInputChange}
                                placeholder={t('example-aircraft-codes')}
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                />
                                {t('active')}
                            </label>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn btn-success">
                            {editingTour ? t('update-tour') : t('create-tour')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={toggleCreate}>
                            {t('cancel')}
                        </button>
                    </div>
                </form>
            ) : showLegsForm && selectedTour ? (
                <div className="legs-manager">
                    <div className="legs-header">
                        <h2>
                            <FontAwesomeIcon icon={faMapMarkedAlt} /> 
                            {t('manage-legs-for')}: {selectedTour.name}
                        </h2>
                        <button 
                            className="btn btn-secondary" 
                            onClick={() => {
                                setShowLegsForm(false);
                                setSelectedTour(null);
                                setTourLegs([]);
                            }}
                        >
                            {t('close')}
                        </button>
                    </div>

                    <form onSubmit={handleAddLeg} className="create-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label>{t('departure-icao')} *</label>
                                <input
                                    type="text"
                                    name="departureIcao"
                                    value={legFormData.departureIcao}
                                    onChange={handleLegInputChange}
                                    required
                                    maxLength={4}
                                />
                            </div>
                            <div className="form-group">
                                <label>Arrival ICAO *</label>
                                <input
                                    type="text"
                                    name="arrivalIcao"
                                    value={legFormData.arrivalIcao}
                                    onChange={handleLegInputChange}
                                    required
                                    maxLength={4}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Estimated Time (minutes)</label>
                                <input
                                    type="number"
                                    name="estimatedTime"
                                    value={legFormData.estimatedTime}
                                    onChange={handleLegInputChange}
                                    min="0"
                                />
                            </div>
                        </div>

                        <button type="submit" className="btn btn-primary">
                            <FontAwesomeIcon icon={faPlus} /> Add Leg
                        </button>
                    </form>

                    <div className="legs-list">
                        <h3>Current Legs ({tourLegs.length})</h3>
                        {tourLegs.length === 0 ? (
                            <p>No legs added yet</p>
                        ) : (
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Order</th>
                                        <th>Departure</th>
                                        <th>Arrival</th>
                                        <th>Distance</th>
                                        <th>Time</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tourLegs.map(leg => (
                                        <tr key={leg.id}>
                                            <td>{leg.order}</td>
                                            <td>{leg.airportDepartureIcao}</td>
                                            <td>{leg.airportArrivalIcao}</td>
                                            <td>{leg.distance || '--'} NM</td>
                                            <td>{leg.estimatedTime || '--'} min</td>
                                            <td>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDeleteLeg(leg.id)}
                                                >
                                                    <FontAwesomeIcon icon={faTrashCan} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            ) : (
                <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Short Description</th>
                            <th>Medal</th>
                            <th>Legs</th>
                            <th>Distance</th>
                            <th>Open Date</th>
                            <th>Close Date</th>
                            <th>Active</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tours.map(tour => (
                            <React.Fragment key={tour.id}>
                                <tr>
                                    <td>{tour.name}</td>
                                    <td>{tour.shortDescription}</td>
                                    <td>{tour.medal?.text || 'None'}</td>
                                    <td>{tour.totalLegs || 0}</td>
                                    <td>{tour.totalDistance || 0} NM</td>
                                    <td>{new Date(tour.open_day).toLocaleDateString()}</td>
                                    <td>{new Date(tour.close_day).toLocaleDateString()}</td>
                                    <td>{tour.isActive ? 'Yes' : 'No'}</td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-info"
                                            onClick={() => toggleExpandTour(tour)}
                                        >
                                            <FontAwesomeIcon icon={expandedTour === tour.id ? faChevronUp : faChevronDown} />
                                        </button>
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={() => openLegsManager(tour)}
                                        >
                                            <FontAwesomeIcon icon={faMapMarkedAlt} />
                                        </button>
                                        <button
                                            className="btn btn-sm btn-warning"
                                            onClick={() => startEditing(tour)}
                                        >
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleDelete(tour.id)}
                                        >
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </button>
                                    </td>
                                </tr>
                                {expandedTour === tour.id && tourLegs.length > 0 && (
                                    <tr>
                                        <td colSpan="9">
                                            <div className="expanded-legs">
                                                <h4>Tour Legs:</h4>
                                                <ol>
                                                    {tourLegs.map((leg, index) => (
                                                        <li key={leg.id}>
                                                            {leg.airportDepartureIcao} → {leg.airportArrivalIcao} 
                                                            {leg.distance && ` (${leg.distance} NM)`}
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                </div>
            )}
        </div>
    );
}